import type { Component, VoidComponent } from "solid-js";
import { createMemo, createSignal, For, getOwner, Match, runWithOwner, Show, Switch, untrack } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../utils/withPagePermissionCheck";
import PermissionService from "../../services/PermissionService";
import { useCustomerState } from "../../state/customer";
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import { useTranslation } from "../../state/i18n";
import DataSourceId from "../../state/DataSourceId";
import ApiService from "../../services/ApiService";
import { Icon } from "../../components/icon/Icon";
import { TableProvider } from "./form/TableProvider";
import Table, { TableActiveFilters, TableBody, TablePagination } from "./form/Table";
import TableFilterButton from "./form/TableFilterButton";
import { defaultStringFilter } from "./form/filter";
import { keyFromSelector } from "i18next";
import Pill from "../../components/pill/Pill";
import { IconButton, TextAndIconButton } from "../../components/button/Button";
import InlineEditor from "../../components/inlineEditor/InlineEditor";
import { MAX_CHARACTERS_SM, MAX_DATA_SOURCE_FAILURE_COUNT } from "../../constants";
import { pickSchema } from "../../utils/validationUtil";
import Text from "../../components/text/Text";
import InlineMessage from "../../components/inlineMessage/InlineMessage";
import FormatDate from "../../components/formatting/FormatDate";
import { Collapsible } from "@ark-ui/solid";
import type { DataSource } from "../../types/DataSource";
import { dataSourcesByType } from "./dataSource/dataSourcesByType";
import DeleteDataSourceConfirmationModal from "./DeleteDataSourceConfirmationModal";
import { useModalManager } from "../../components/modal/ModalManager";
import { IconTile } from "../../components/icon/IconTile";
import { isArray, isEmpty, isNil, isPlainObject } from "lodash-es";
import { baseDataSourceSchema } from "../../utils/validation/dataSource";
import GenericFailureModal from "../GenericFailureModal";
import type { DataSourceEntry } from "../../types/DataSourceEntry";
import EditDataSourceModal from "./dataSource/EditDataSourceModal";
import { NON_RETRYABLE_STATUS_CODES } from "../../utils/apiUtil";
import { ResourceNotFoundContent } from "../ResourceNotFoundPage";

const dataSourceEntryStringFilter = defaultStringFilter.filter((definition) => definition.operator !== "isFilledIn");

const DataSourcePage: Component<RouteSectionProps<never>> = (props) => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const dataSourceId = createMemo(() => DataSourceId.from(props.params.dataSourceId!));
  const dataSourceQuery = createMemo(() => ApiService.getDataSource(dataSourceId()));

  return (
    <>
      <Show
        when={!isNil(dataSourceQuery().error) && NON_RETRYABLE_STATUS_CODES.includes(dataSourceQuery().error!.status)}
      >
        <ResourceNotFoundContent
          resource={keyFromSelector(($) => $.RESOURCE_DATASOURCE)}
          illustration={{ primaryIcon: "database" }}
        />
      </Show>
      <Show when={dataSourceQuery().data}>
        {(dataSource) => (
          <div class="flex h-full flex-col gap-6 pb-6">
            <Breadcrumb
              currentLabel={dataSource().name}
              crumbs={[
                {
                  label: t(($) => $.DATASOURCE_PAGE_BACK_TO_DATASOURCES),
                  href: `/customers/${customer.customerId}/data-sources`,
                },
              ]}
            />

            <Header dataSource={dataSource()} />

            <div class="flex min-h-0 grow flex-col gap-4">
              <Show when={!dataSource().enabled && dataSource().failedExecutionCount >= MAX_DATA_SOURCE_FAILURE_COUNT}>
                <InlineMessage
                  variant="danger"
                  label={t(($) => $.DATASOURCE_PAGE_AUTOMATIC_IMPORT_DISABLED_MESSAGE, {
                    count: MAX_DATA_SOURCE_FAILURE_COUNT,
                  })}
                  action={{
                    label: t(($) => $.DATASOURCE_PAGE_AUTOMATIC_IMPORT_DISABLED_ACTION),
                    onClick: () => {
                      runWithOwner(owner, () =>
                        ApiService.enableDataSource().mutateAsync(dataSource().id, {
                          onError: (err) =>
                            open({
                              content: () => <GenericFailureModal error={err} />,
                            }),
                        }),
                      );
                    },
                  }}
                />
              </Show>

              <ImportStatus dataSource={dataSource()} />

              <Show
                when={dataSource().updateStatus !== "NEVER"}
                fallback={
                  <div class="flex grow flex-col items-center justify-center">
                    <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
                    <span class="text-medium-regular text-primary">
                      {t(($) => $.DATASOURCE_ENTRIES_TABLE_LOADING_DESCRIPTION)}
                    </span>
                  </div>
                }
              >
                <TableProvider>
                  <Table<DataSourceEntry>
                    fetchData={(options) => ApiService.getDataSourceEntries(dataSource().id, options)}
                    columns={dataSource().columnMapping.map(({ id, label }) => ({
                      id: `data.${id}`,
                      accessorKey: `data.${id}`,
                      enableSorting: false,
                      enableColumnFilter: true,
                      cell: (context) => {
                        const value = context.getValue();
                        return isPlainObject(value) || isArray(value) ? JSON.stringify(value) : value;
                      },
                      meta: {
                        name: label,
                        filter: {
                          type: "string",
                          // Not all data types can be filtered as strings, but is acceptable for now DEV-8464
                          definition: dataSourceEntryStringFilter,
                        },
                      },
                    }))}
                    defaultVisibleColumns={dataSource().columnMapping.map(({ id }) => `data.${id}`)}
                    persistKey={`datasource-entries-${dataSource().id.toString()}`}
                    bulkSelect={false}
                  >
                    <div class="flex gap-4">
                      <TableFilterButton />
                    </div>
                    <TableActiveFilters />
                    <div class="flex min-h-0 flex-col">
                      <TableBody
                        emptyFilteredText={keyFromSelector(
                          ($) => $.DATASOURCE_ENTRIES_TABLE_EMPTY_FILTERED_DESCRIPTION,
                        )}
                        loadingText={keyFromSelector(($) => $.DATASOURCE_ENTRIES_TABLE_LOADING_DESCRIPTION)}
                      />
                      <TablePagination
                        pageSizeLabel={keyFromSelector(($) => $.DATASOURCE_ENTRIES_TABLE_PAGE_SIZE_LABEL)}
                      />
                    </div>
                  </Table>
                </TableProvider>
              </Show>
            </div>
          </div>
        )}
      </Show>
    </>
  );
};

type HeaderProps = {
  dataSource: DataSource;
};
const Header: VoidComponent<HeaderProps> = (props) => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const [isEditingName, setIsEditingName] = createSignal(false);

  const definition = createMemo(() => dataSourcesByType[props.dataSource.type]);

  return (
    <div class="flex items-center gap-6">
      <div class="flex min-w-0 gap-2.5">
        <div class="icon-secondary">
          <IconTile icon={definition().icon} size="xl" border />
        </div>

        <div class="min-w-0">
          <div class="text-header-xl text-primary">
            <InlineEditor
              value={props.dataSource.name}
              setValue={(value) => {
                const dataSource = untrack(() => props.dataSource);
                const { toDto } = definition();

                runWithOwner(owner, () =>
                  ApiService.updateDataSource(dataSource.id).mutate(
                    {
                      ...toDto(dataSource.configuration),
                      name: value,
                    },
                    {
                      onError: (err) => open({ content: () => <GenericFailureModal error={err} /> }),
                    },
                  ),
                );
              }}
              label={t(($) => $.DATASOURCE_PAGE_EDIT_NAME_ARIA_LABEL)}
              open={isEditingName()}
              setOpen={setIsEditingName}
              maxLength={MAX_CHARACTERS_SM}
              schema={pickSchema(baseDataSourceSchema, "name")}
              displayView={() => (
                <div class="flex items-center gap-1">
                  <Text as="h2" text={props.dataSource.name} truncate />
                  <IconButton
                    icon="pencil"
                    size="medium"
                    variant="tertiary"
                    aria-label={t(($) => $.EDIT_ARIA)}
                    onClick={() => setIsEditingName(true)}
                  />
                </div>
              )}
            />
          </div>

          <Switch>
            <Match when={props.dataSource.searchType === "LIVE"}>
              <Pill variant="neutral" label={t(($) => $.DATASOURCE_PAGE_SEARCH_TYPE_LIVE)} icon="statusOnline" />
            </Match>
            <Match when={props.dataSource.searchType === "LOCAL_FALLBACK"}>
              <Pill
                variant="neutral"
                label={t(($) => $.DATASOURCE_PAGE_SEARCH_TYPE_LOCAL_FALLBACK)}
                icon="statusOffline"
              />
            </Match>
          </Switch>
        </div>
      </div>

      <div class="ml-auto flex gap-2">
        <Show
          when={definition().extraAction}
          fallback={
            <TextAndIconButton
              variant="secondary"
              size="large"
              text="Edit"
              icon="pencil"
              onClick={() => {
                open({ content: () => <EditDataSourceModal dataSource={props.dataSource} /> });
              }}
            />
          }
        >
          {(ExtraAction) => (
            <>
              {ExtraAction()({ dataSource: props.dataSource })}
              <IconButton
                variant="secondary"
                size="large"
                icon="pencil"
                aria-label={t(($) => $.EDIT_ARIA)}
                onClick={() => {
                  open({ content: () => <EditDataSourceModal dataSource={props.dataSource} /> });
                }}
              />
            </>
          )}
        </Show>

        <IconButton
          variant="secondary"
          size="large"
          icon="trash"
          aria-label={t(($) => $.DELETE_ARIA)}
          onClick={() => {
            open({
              content: () => (
                <DeleteDataSourceConfirmationModal
                  customerId={customer.customerId}
                  dataSourceId={props.dataSource.id}
                />
              ),
            });
          }}
        />
      </div>
    </div>
  );
};

type ImportStatusProps = {
  dataSource: DataSource;
};
const ImportStatus: VoidComponent<ImportStatusProps> = (props) => {
  const { t } = useTranslation();

  const ImportStatusHeader = () => {
    const icon = createMemo(() => {
      switch (props.dataSource.updateStatus) {
        case "NEVER":
        case "SUCCESS":
          return "checkCircle";
        case "FAILURE":
          return "exclamationCircle";
      }
    });

    return (
      <div class="flex gap-2">
        <div
          class="flex size-9 items-center justify-center rounded-full"
          classList={{
            "bg-surface-status-subtle-success icon-status-success": props.dataSource.updateStatus === "SUCCESS",
            "bg-surface-status-subtle-danger icon-status-error": props.dataSource.updateStatus === "FAILURE",
          }}
        >
          <Icon name={icon()} type="outline" size="xl" />
        </div>
        <div class="text-left">
          <p class="text-medium-semibold text-primary">
            <Switch>
              <Match when={props.dataSource.updateStatus === "SUCCESS"}>
                {t(($) => $.DATASOURCE_PAGE_IMPORT_STATUS_SUCCESS)}
              </Match>
              <Match when={props.dataSource.updateStatus === "FAILURE"}>
                {t(($) => $.DATASOURCE_PAGE_IMPORT_STATUS_FAILURE)}
              </Match>
            </Switch>
          </p>

          <Show when={props.dataSource.lastUpdatedAt}>
            {(lastUpdatedAt) => (
              <p class="text-base-regular text-secondary">
                <FormatDate value={lastUpdatedAt()} />
              </p>
            )}
          </Show>
        </div>
      </div>
    );
  };

  return (
    <Show when={props.dataSource.updateStatus === "SUCCESS" || props.dataSource.updateStatus === "FAILURE"}>
      <div class="rounded-sm border border-subtle bg-surface-default">
        <Show
          when={!isEmpty(props.dataSource.lastUpdateWarningMessages)}
          fallback={
            <div class="p-6">
              <ImportStatusHeader />
            </div>
          }
        >
          <Collapsible.Root>
            <Collapsible.Context>
              {(context) => (
                <>
                  <Collapsible.Trigger
                    class="w-full p-6 focus-visible:outline-default"
                    classList={{ "hover:bg-surface-hover": !context().open }}
                    aria-label={
                      context().open
                        ? t(($) => $.DATASOURCE_PAGE_IMPORT_STATUS_COLLAPSE)
                        : t(($) => $.DATASOURCE_PAGE_IMPORT_STATUS_EXPAND)
                    }
                  >
                    <div class="flex items-center gap-4">
                      <ImportStatusHeader />

                      <div class="ml-auto flex w-46 items-center justify-between">
                        <Show when={props.dataSource.lastUpdateWarningMessages.length > 0}>
                          <Pill
                            variant="warning"
                            icon="exclamation"
                            label={t(($) => $.DATASOURCE_IMPORT_WARNINGS, {
                              count: props.dataSource.lastUpdateWarningMessages.length,
                            })}
                          />
                        </Show>

                        <Collapsible.Indicator>
                          <div
                            class="shrink-0 transition-transform duration-200 ease-linear"
                            classList={{ "rotate-180": context().open }}
                          >
                            <Icon name="chevronDown" type="solid" size="lg" />
                          </div>
                        </Collapsible.Indicator>
                      </div>
                    </div>
                  </Collapsible.Trigger>

                  {/* oxlint-disable-next-line better-tailwindcss/no-restricted-classes */}
                  <Collapsible.Content class="grid grid-rows-[1fr] transition-all duration-250 ease-out closed:grid-rows-[0fr] closed:duration-200 closed:ease-in">
                    <ol
                      class="max-h-92 divide-y divide-subtle overflow-y-auto px-6 text-base-regular text-secondary focus-visible:outline-default"
                      classList={{
                        "mb-6": context().open,
                      }}
                    >
                      <For each={props.dataSource.lastUpdateWarningMessages}>
                        {(message, index) => (
                          <li class="flex gap-6 p-3 pl-4 first:pt-0 last:pb-0">
                            <p>{index() + 1}</p>
                            <p>{message}</p>
                          </li>
                        )}
                      </For>
                    </ol>
                  </Collapsible.Content>
                </>
              )}
            </Collapsible.Context>
          </Collapsible.Root>
        </Show>
      </div>
    </Show>
  );
};

const ProtectedDataSourcePage = withPagePermissionCheck(DataSourcePage, PermissionService.canManageDataSources);

export default ProtectedDataSourcePage;
